import { useState } from 'react';
import { apiFetch } from '../lib/api';
import PasswordInput from './PasswordInput';
import GithubPatPermissionsHelp from './GithubPatPermissionsHelp';
import PartnerClientApprovalMatrix, { compactPartnerMatrix, fullPartnerMatrix, type PartnerMatrix } from './clientApprovals/PartnerClientApprovalMatrix';
import { clientApprovalTypeLabel, requiredLabel } from '../lib/clientApprovals';

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  /** #3185 2c (R1 P2-1): the partner's stored default matrix, the form's starting point. */
  partnerMatrix?: Record<string, boolean>;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  backgroundColor: 'var(--input-bg)',
  color: 'var(--text)',
  fontSize: 14,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--text)',
};

export default function ManagedOnboardingForm({ onSuccess, onCancel, partnerMatrix }: Props) {
  const [orgName, setOrgName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [githubOrg, setGithubOrg] = useState('');
  const [githubToken, setGithubToken] = useState('');
  // #3185 APPROVE.2 2c (R1 P2-1, ruling D8): this client's approval matrix,
  // from the partner's default. Sent as clientApprovalMatrix, which becomes the
  // new workspace's defaults (partner.router.ts managed create).
  const [matrix, setMatrix] = useState<PartnerMatrix>(() => fullPartnerMatrix(partnerMatrix));
  const [step, setStep] = useState<'form' | 'review'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canProceed = orgName.trim() && adminName.trim() && adminEmail.trim();

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await apiFetch('/partner/clients/managed', {
        method: 'POST',
        body: JSON.stringify({
          orgName: orgName.trim(),
          adminName: adminName.trim(),
          adminEmail: adminEmail.trim(),
          timezone,
          githubOrg: githubOrg.trim() || undefined,
          githubToken: githubToken.trim() || undefined,
          approvalConfig: {
            clientApprovalMatrix: compactPartnerMatrix(matrix),
            // The welcome email still prints these three (APPROVE.5 rewrites it).
            // Derived from the matrix, so the email says what the workspace gets;
            // the server never translates them into the matrix (D8).
            requireClientPRDApproval: matrix.prd,
            requireClientQAApproval: matrix.qa_report,
            requireClientDeploymentApproval: matrix.deployment_runbook,
          },
        }),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message ?? 'Failed to create workspace');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'review') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text)' }}>Review &amp; Confirm</h2>

        <div className="border rounded-lg p-4 mb-4 space-y-2" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}>
          <Row label="Organisation" value={orgName} />
          <Row label="Admin" value={`${adminName} (${adminEmail})`} />
          <Row label="Timezone" value={timezone} />
          {githubOrg && <Row label="GitHub Org" value={githubOrg} />}
          {githubToken && <Row label="GitHub Token" value="****" />}
          {Object.entries(matrix).filter(([, v]) => v).map(([t]) => (
            <Row key={t} label={`${clientApprovalTypeLabel(t)} client approval`} value={requiredLabel(true)} />
          ))}
          {!Object.values(matrix).some(Boolean) && <Row label="Client approval" value="Not required for any deliverable" />}
        </div>

        {error && <p style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</p>}

        <div className="flex gap-3">
          <button onClick={() => setStep('form')} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer' }}>
            Back
          </button>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: loading ? 'wait' : 'pointer', flex: 1, fontWeight: 600 }}>
            {loading ? 'Creating...' : 'Create Workspace'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text)' }}>Set Up Client Workspace</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Create a managed workspace for your client. They will receive an access email.</p>

      <fieldset className="space-y-4 mb-6" style={{ border: 'none', padding: 0 }}>
        <legend className="text-sm font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Client Details</legend>
        <div>
          <label style={labelStyle}>Organisation Name *</label>
          <input style={inputStyle} value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Acme Corp" />
        </div>
        <div>
          <label style={labelStyle}>Admin Full Name *</label>
          <input style={inputStyle} value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Jane Smith" />
        </div>
        <div>
          <label style={labelStyle}>Admin Email *</label>
          <input style={inputStyle} type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="jane@acme.com" />
        </div>
      </fieldset>

      <fieldset className="space-y-4 mb-6" style={{ border: 'none', padding: 0 }}>
        <legend className="text-sm font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Configuration</legend>
        <div>
          <label style={labelStyle}>Timezone</label>
          <select style={inputStyle} value={timezone} onChange={e => setTimezone(e.target.value)}>
            {['UTC', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Australia/Sydney'].map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>GitHub Organisation (optional)</label>
          <input style={inputStyle} value={githubOrg} onChange={e => setGithubOrg(e.target.value)} placeholder="acme-corp" />
        </div>
        <div>
          <label style={labelStyle}>GitHub Token (optional)</label>
          <PasswordInput style={{ ...inputStyle, fontFamily: 'monospace' }} value={githubToken} onChange={e => setGithubToken(e.target.value)} placeholder="ghp_… or github_pat_…" />
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Used for repository access. Stored encrypted.</p>
          <GithubPatPermissionsHelp />
        </div>
      </fieldset>

      <fieldset className="space-y-3 mb-6" style={{ border: 'none', padding: 0 }}>
        <legend className="text-sm font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Client approvals</legend>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Starts from your default. Becomes this client&rsquo;s workspace default.
        </p>
        <PartnerClientApprovalMatrix value={matrix} onChange={setMatrix} />
      </fieldset>

      <div className="flex gap-3">
        <button onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer' }}>
          Cancel
        </button>
        <button disabled={!canProceed} onClick={() => setStep('review')} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: canProceed ? '#7c3aed' : '#555', color: '#fff', cursor: canProceed ? 'pointer' : 'not-allowed', flex: 1, fontWeight: 600 }}>
          Review
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
